import React, { useEffect, useState } from 'react';
import HeaderImage from './HeaderImage';
import { brandTextFor } from '@charitystack/design-tokens/brand';
import { UNBRANDED_THEME } from '../styling/unbrandedTheme.js';
import OrgLogoTile from './OrgLogoTile.jsx';

/**
 * CampaignDetailHeader — Shared campaign header section.
 *
 * Renders: Title → "By {organizer}" → Header Image → Fundraising Bar (optional)
 *
 * This is the single source of truth for campaign header styling, used by:
 * - hosted-page CampaignHeader.jsx (LeftPanel on hosted donation pages)
 * - donation-elements OverlayCampaignDetail.jsx (overlay mobile layout)
 *
 * The fundraising bar is passed as a render prop because it lives in
 * donation-elements, not @charitystack/shared. Each consumer provides
 * their own FundraisingBar instance.
 *
 * @param {string} formName - Campaign/form title
 * @param {string} organizedBy - Organizer name (falls back to merchantName)
 * @param {string} merchantName - Nonprofit name (used for image URL + "By" fallback)
 * @param {string} formID - Form ID for image filename
 * @param {string} formType - 'EVENT' | other — determines image folder path
 * @param {string} imageBaseUrl - Base URL for image hosting (VITE_GLOBAL_IMAGE_URL)
 * @param {string} [headerImage] - Direct image URL (preview mode)
 * @param {Object} [brandColors] - Brand color palette
 * @param {boolean} [previewMode=false] - Dashboard preview mode
 * @param {boolean} [showFundraisingBar=false] - Whether to render the bar
 * @param {Function} [renderFundraisingBar] - Render prop for FundraisingBar
 * @param {string} [imageSizes] - `sizes` for the hero's responsive variants (CON-72). Leave
 *   unset on the hosted page (HeaderImage carries the measured rail layout); the widget passes a
 *   viewport-relative value because the host page decides the iframe's width.
 * @param {'h1'|'h2'|'h3'} [headingLevel='h1'] - Heading tag for the campaign title.
 *   Defaults to 'h1' (correct on real hosted pages, where the campaign name IS the
 *   page's primary heading). The marketing-website hero/product demos pass 'h2' so the
 *   embedded demo title doesn't duplicate the marketing page's own H1 (SEO).
 * @param {string} [logo] - Direct logo URL; else the S3 `<merchantName>_Logo` object (OrgLogoTile).
 * @param {string} [logoVersion] - Logo cache version.
 * @param {string} [s3LogoUrl] - S3 base override for the logo.
 * @param {'always'|'responsive'|'never'} [bylineLogo='always'] - Where the by-line draws the
 *   24 logo tile: always, only at md and up (the mobile header row carries it below), or never.
 * @param {boolean} [compact=false] - Title stays 24/30 at every width (narrow panes).
 */
function CampaignDetailHeader({
  formName,
  organizedBy,
  merchantName,
  formID,
  formType,
  imageBaseUrl,
  headerImage,
  imageVersion,
  brandColors,
  previewMode = false,
  showFundraisingBar = false,
  renderFundraisingBar,
  imageSizes,
  headingLevel = 'h1',
  logo,
  logoVersion,
  s3LogoUrl,
  bylineLogo = 'always',
  compact = false,
}) {
  const [organizedByValue, setOrganizedByValue] = useState('');

  useEffect(() => {
    setOrganizedByValue(organizedBy === '' ? merchantName : organizedBy);
  }, [merchantName, organizedBy]);

  // Set default formName in preview mode if empty
  const displayFormName = previewMode && !formName ? 'Enter Form Title' : formName;

  // Render the title with the requested heading tag (defaults to <h1>). Styling is
  // driven entirely by the className below, so the tag choice is purely semantic.
  const HeadingTag = headingLevel;

  // Extract primary brand color for fallback image SVG. No palette paints the
  // neutral action fill, as an unbranded organisation does everywhere (CON-331).
  let brandColor = brandColors?.['accent-50'] || UNBRANDED_THEME.lightColor;
  if (typeof brandColor !== 'string' || !brandColor.startsWith('#')) {
    brandColor = UNBRANDED_THEME.lightColor;
  }
  // The "By <organisation>" line is brand-coloured TEXT (design.md §1.5).
  // Measured on the hosted page before this: 1.24:1 at a pale brand, on the
  // string that names the nonprofit. `brandColor` above stays the FILL used for
  // the fallback header image and is deliberately NOT guarded — a fill is not
  // text, and guarding it would darken the image behind the heading too.
  const brandTextColor = brandTextFor(brandColors, { fallback: brandColor });

  // CON-518 (Paper "Org identity · By line"): the logo moves out of its own row into a 24
  // tile at the start of the by-line. Below md the hosted page and the overlay draw it in
  // the 56 header row instead, so the by-line drops it there ('responsive').
  const showLogoTile = bylineLogo !== 'never';
  const logoTileClass = bylineLogo === 'responsive' ? 'hidden md:inline-flex' : 'inline-flex';
  const titleClass = compact
    ? 'text-[length:var(--cs-text-2xl)] leading-[var(--cs-leading-30)]'
    : 'text-[length:var(--cs-text-2xl)] leading-[var(--cs-leading-30)] md:text-[length:var(--cs-text-display)] md:leading-[var(--cs-leading-38)]';

  return (
    <div className="relative mx-auto w-full flex flex-col gap-[var(--cs-space-4)]">
      {/* Title + Organized by */}
      <div className="flex flex-col gap-[var(--cs-space-1)] md:gap-[var(--cs-space-2)]">
        <HeadingTag
          className={`m-0 pb-0 font-semibold tracking-[var(--cs-tracking-tight)] text-[color:var(--cs-text-primary)] ${titleClass}`}
        >
          {displayFormName}
        </HeadingTag>
        {organizedByValue && (
          <p className="m-0 flex items-center gap-[var(--cs-space-1)] font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)]">
            {showLogoTile && (
              <span className={`${logoTileClass} mr-[var(--cs-space-1)]`}>
                <OrgLogoTile
                  merchantName={merchantName}
                  logo={logo}
                  logoVersion={logoVersion}
                  s3LogoUrl={s3LogoUrl}
                  size="sm"
                />
              </span>
            )}
            <span className="font-normal text-[color:var(--cs-text-secondary)]">By</span>
            <span
              className="font-medium"
              style={{ color: `var(--cs-brand-text, ${brandTextColor})` }}
            >
              {organizedByValue}
            </span>
          </p>
        )}
      </div>

      {/* Header Image */}
      <HeaderImage
        key={previewMode && headerImage ? headerImage : `${merchantName}-${formID}`}
        imageBaseUrl={imageBaseUrl}
        merchantName={merchantName}
        formID={formID}
        formType={formType}
        alt="Fundraiser Header"
        headerImage={headerImage}
        previewMode={previewMode}
        brandColor={brandColor}
        title={displayFormName}
        imageVersion={imageVersion}
        sizes={imageSizes}
      />

      {/* Fundraising Bar — render prop to avoid importing donation-elements in shared */}
      {showFundraisingBar && renderFundraisingBar && renderFundraisingBar()}
    </div>
  );
}

export default CampaignDetailHeader;
